import { Actions as COMPANY_ACTIONS } from "@/redux/actions/companyActions";
import { Actions as EMPLOYEE_ACTIONS } from "@/redux/actions/employeeActions";
import { Actions as PAYMENT_ACTIONS } from "@/redux/actions/paymentActions";
import {
	ArrowUpRightIcon,
	BuildingOfficeIcon,
	CakeIcon,
	CalendarIcon,
	CheckBadgeIcon,
	ChevronDownIcon,
	ListBulletIcon,
	UserGroupIcon,
	XMarkIcon,
} from "@heroicons/react/20/solid";
import { ArrowTopRightOnSquareIcon } from "@/icons/outline";
import ProgressBar from "@/components/common/ProgressBar";
import { camelCase, merge } from "lodash";
import { useMemo, useState } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { industryOptions } from "../company-settings/config/IndustryOptions";
import { validateFloat } from "@/components/common/validation/Validators";

import BuddyLogo from "../../images/buddy-face.png";
import { useEffect } from "react";

const SetupChecklist = ({
	settings,
	jurisdiction,
	industry,
	companyName,
	showPricingModal,
	periods,
	employees,
	cola,
	updateSetupToggles,
	employeesGetListWithSalary,
	updateSetupTargets,
	selectedMethod,
	getCola,
	creditBalance,
	updateCompanyDetails,
	updateSetupProgress,
	principal,
	pensions,
	asset,
}) => {
	const [toggle, setToggle] = useState({});
	const [modalStatus, setModalStatus] = useState(false);
	const { targets } = settings?.setup || {};
	const employeeTarget = targets?.employees || 0;
	const employeeProgress = employees?.filter((employee) => employee?.isIncomplete == false).length || 0;
	const payPeriodTarget = targets?.payPeriods || 0;
	const payPeriodProgress = periods?.length || 0;

	useEffect(() => {
		switch (jurisdiction) {
			case "MT":
				getCola();
				break;
		}
		employeesGetListWithSalary({
			from: `${new Date().getFullYear()}-01-01`,
			to: `${new Date().getFullYear()}-12-31`,
		});
	}, []);

	const [renderChecklist, stepsRequired, stepsCompleted, steps] = useMemo(() => {
		const checklist = Checklist({
			jurisdiction,
			settings,
			employees,
			periods,
			cola,
			selectedMethod,
			creditBalance,
			principal,
			pensions,
			asset,
		});
		let stepsRequired = 0;
		let stepsCompleted = 0;
		let steps = [];
		checklist?.map(({ list }) => {
			list?.map(({ title, progress, target }) => {
				if (target === progress) {
					steps.push(title);
					stepsCompleted++;
				}
				stepsRequired++;
			});
		});
		return [checklist, stepsRequired, stepsCompleted, steps];
	}, [
		JSON.stringify(settings),
		jurisdiction,
		periods?.length,
		employees?.length,
		cola?.length,
		selectedMethod?.value,
		creditBalance,
	]);

	useEffect(() => {
		if (!steps?.length) return;
		if (steps?.length !== settings?.setup?.steps?.length) updateSetupProgress(steps);
	}, [steps?.length != settings?.setup?.steps?.length]);

	return (
		<>
			<SetTargetModal
				closeModal={() => setModalStatus(false)}
				modalStatus={modalStatus}
				updateSetupTargets={updateSetupTargets}
				existingTarget={(() => {
					if (modalStatus === "Employees") return employeeTarget;
					if (modalStatus === "Pay Periods") return payPeriodTarget;
				})()}
			/>
			<div className="content bg-gray-lighter">
				<div className="row">
					<div className="col-sm-12">
						<div className="pull-left">
							<h1 className="page-heading">Getting Started</h1>
						</div>
					</div>
				</div>
				<div
					className="row items-push text-uppercase"
					style={{ paddingTop: 0, paddingBottom: 0 }}
				>
					<div className="col-xs-6 col-sm-3">
						<div
							className="font-w700 text-gray-darker"
							id="total"
						>
							Company
						</div>
						<div className="text-muted animated fadeIn">
							<small>
								<BuildingOfficeIcon style={{ height: "1em" }} /> {industry}
							</small>
						</div>
						<Link
							to="/organisation/settings"
							className="font-w300 text-primary animated"
							style={{ width: "max-content", display: "block" }}
						>
							<h4>{companyName}</h4>
						</Link>
					</div>
					<div className="col-xs-6 col-sm-3">
						<div
							className="font-w700 text-gray-darker"
							id="active"
						>
							Steps Completed
						</div>
						<div className="text-muted animated fadeIn">
							<small>
								<ListBulletIcon style={{ height: "1em" }} /> To Date
							</small>
						</div>
						<Link
							to="/organisation/setup"
							className="font-w300 text-primary animated"
							style={{ width: "min-content", display: "block" }}
						>
							<h4>
								{stepsCompleted}/{stepsRequired}
							</h4>
						</Link>
						<ProgressBar
							percentage={(stepsCompleted / stepsRequired) * 100}
							style={{ height: "1.5rem", marginRight: "1rem" }}
							color={"rgb(234,97,28)"}
							showContent={true}
						>
							{stepsCompleted}/{stepsRequired}
						</ProgressBar>
					</div>
					<div className="col-xs-6 col-sm-3">
						<div
							className="font-w700 text-gray-darker"
							id="tax-paid"
						>
							Employees Imported
						</div>
						<div className="text-muted animated fadeIn">
							<small>
								<UserGroupIcon style={{ height: "1em" }} /> Now
							</small>
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									setModalStatus("Employees");
								}}
								style={{ float: "right", color: "rgb(234,97,28)" }}
							>
								<ArrowUpRightIcon style={{ height: "1.5rem", marginRight: "0.5rem" }} />
								Set Target
							</a>
						</div>
						<Link
							to="/organisation/people"
							className="font-w300 text-primary animated"
							style={{ width: "min-content", display: "block" }}
						>
							<h4>
								{employeeProgress}/{employeeTarget}
							</h4>
						</Link>
						<ProgressBar
							percentage={
								targets?.employees !== undefined && employeeProgress == employeeTarget
									? 100
									: (employeeProgress / employeeTarget) * 100
							}
							style={{ height: "1.5rem", marginRight: "1rem" }}
							color={"rgb(234,97,28)"}
							showContent={true}
						></ProgressBar>
					</div>
					{jurisdiction != "UK" ? (
						<div className="col-xs-6 col-sm-3">
							<div
								className="font-w700 text-gray-darker"
								id="gross-pay"
							>
								Payrolls Imported
							</div>
							<div className="text-muted animated fadeIn">
								<small>
									<CalendarIcon style={{ height: "1em" }} /> To Date
								</small>
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										setModalStatus("Pay Periods");
									}}
									style={{ float: "right", color: "rgb(234,97,28)" }}
								>
									<ArrowUpRightIcon style={{ height: "1.5rem", marginRight: "0.5rem" }} />
									Set Target
								</a>
							</div>
							<Link
								to="/organisation/billing"
								className="font-w300 text-primary animated"
								style={{ width: "min-content", display: "block" }}
							>
								<h4>
									{payPeriodProgress}/{payPeriodTarget}
								</h4>
							</Link>
							<ProgressBar
								percentage={
									targets?.payPeriods !== undefined && payPeriodProgress == payPeriodTarget
										? 100
										: (payPeriodProgress / payPeriodTarget) * 100
								}
								style={{ height: "1.5rem", marginRight: "1rem" }}
								color={"rgb(234,97,28)"}
								showContent={true}
							></ProgressBar>
						</div>
					) : null}
				</div>
			</div>
			<div className="content">
				{stepsRequired === stepsCompleted ? (
					<div
						style={{
							borderRadius: "1rem",
							padding: "2rem",
							border: "1px solid rgb(244,244,244)",
							boxShadow: "1px 1px 5px 0px rgba(0,0,0,0.1)",
							display: "flex",
							flexDirection: "row",
							justifyContent: "left",
							marginBottom: "1rem",
							alignItems: "center",
						}}
					>
						<CakeIcon
							style={{
								height: "5rem",
								marginRight: "2rem",
								display: "block",
								background: "rgb(234,97,28)",
								padding: "1rem",
								borderRadius: "50%",
								color: "white",
							}}
						/>
						<div>
							<h3>You're all set!</h3>
							You can now run your first payroll.
						</div>
						<div style={{ flex: "auto" }}>
							<button
								className="btn btn-primary pull-right"
								style={{}}
								onClick={() => {
									updateCompanyDetails({
										org: settings?.org,
										status: "Active",
									});
								}}
							>
								Go Live
								<img
									src={BuddyLogo}
									style={{ marginLeft: "1rem", height: "2rem" }}
								/>
							</button>
						</div>
					</div>
				) : null}
				<div
					style={{
						borderRadius: "1rem",
						padding: "2rem",
						border: "1px solid rgb(244,244,244)",
						boxShadow: "1px 1px 5px 0px rgba(0,0,0,0.1)",
						display: "grid",
						gridTemplateColumns: "repeat(3, 1fr)",
						gridGap: "1rem",
					}}
				>
					{renderChecklist?.map(({ title, list }) => {
						return (
							<>
								<div
									style={{
										gridColumn: "span 3",
									}}
								>
									{/* Section Header Ex. The Basics */}
									<h3 style={{ textAlign: "left" }}>{title}</h3>
								</div>
								{list?.map(({ title, tag, list, progress, target, optionals, total }) => {
									const success = progress === target;
									const totalChecked = optionals + progress;
									const toggleKey = toggle[tag];
									const defaultToggle =
										typeof toggleKey === "undefined" ? totalChecked === total : toggleKey;
									return (
										<div
											style={{
												borderRadius: "1rem",
												border: "1px solid rgb(244,244,244)",
												boxShadow: "1px 1px 5px 0px rgba(0,0,0,0.1)",
												height: defaultToggle ? "65.42px" : "auto",
											}}
										>
											<div
												style={{
													borderBottom: "1px solid rgb(244,244,244)",
													padding: "1rem 1rem 1rem 1rem",
													borderRadius: "1rem 1rem 0rem 0rem",
													backgroundColor: success ? "rgb(0, 187, 135)" : "rgb(234,97,28)",
													fontSize: "1.5rem",
													color: "white",
													fontWeight: "bolder",
												}}
											>
												{/* Card Title Ex. Company Details */}
												{title}
												<span style={{ textAlign: "right" }}>
													{success ? (
														<div style={{ float: "right" }}>
															<ChevronDownIcon
																onClick={() =>
																	setToggle({ ...toggle, [tag]: !toggleKey })
																}
																style={{
																	height: "1.5em",
																	float: "left",
																	cursor: "pointer",
																	transform: !defaultToggle
																		? "rotate(180deg)"
																		: "rotate(0deg)",
																	transition: "transform 0.2s ease-in-out",
																	userSelect: "none",
																}}
															/>
														</div>
													) : null}

													<div style={{ float: "right" }}>
														{totalChecked}/{total}
													</div>
													{progress === target && (
														<CheckBadgeIcon
															style={{ height: "2rem", float: "right", color: "white" }}
														/>
													)}
												</span>
											</div>
											{defaultToggle ? null : (
												<div
													style={{
														padding: "1rem 1rem 1rem 1rem",
														height: "calc(100% - 42.42px - 36px)",
													}}
												>
													{list?.map(({ title, value }) => {
														return (
															// Checklist Ex. Company Name
															<Checkbox
																label={title}
																checked={value.checked}
																action={value.action}
																disabled={value.disabled}
																required={value.required}
																onClick={(tag) => {
																	updateSetupToggles(tag);
																}}
															/>
														);
													})}
												</div>
											)}
											<div
												style={{
													borderTop: "1px solid rgb(244,244,244)",
													padding: "1rem 0rem 1rem 1rem",
												}}
											>
												{defaultToggle ? null : (
													<ProgressBar
														percentage={(progress / target) * 100}
														style={{ height: "1.5rem", marginRight: "1rem" }}
														color={success ? "rgb(0, 187, 135)" : "rgb(234,97,28)"}
													/>
												)}
											</div>
										</div>
									);
								})}
							</>
						);
					})}
				</div>
			</div>
		</>
	);
};

const Checkbox = ({ checked, action, disabled, label, required, onClick }) => {
	return (
		<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
			<div style={{ float: "left" }}>
				<label
					className={`css-input css-checkbox css-checkbox-${
						checked ? "default" : "primary"
					} remove-margin-t remove-margin-b`}
					style={{
						color: checked ? "rgb(200,200,200)" : undefined,
						textDecoration: checked ? "line-through" : undefined,
					}}
				>
					<input
						type="checkbox"
						checked={checked}
						disabled={disabled}
						onClick={() => {
							onClick && onClick(label);
						}}
					/>
					<span></span>
					{label}
					{required ? (
						<span style={{ color: checked ? "rgb(200,200,200)" : "rgb(234,97,28)" }}>*</span>
					) : null}
					&nbsp;
				</label>
			</div>
			<Link
				to={checked ? undefined : action.to || "#"}
				target={action?.to?.includes("https://") ? "_blank" : undefined}
				onClick={(e) => {
					if (checked || action.onClick) {
						e.preventDefault();
						e.stopPropagation();
					}
					if (!checked && action?.onClick) {
						action.onClick();
					}
				}}
				style={{
					color: checked ? "rgb(200,200,200)" : "rgb(234,97,28)",
					textDecoration: checked ? "line-through" : undefined,
					cursor: checked ? "default" : "pointer",
				}}
			>
				{action ? action?.label : null}
				{action?.to?.includes("https://") && (
					<ArrowTopRightOnSquareIcon style={{ height: "1.25rem", marginLeft: "0.5rem" }} />
				)}
			</Link>
		</div>
	);
};

export const Checklist = ({
	jurisdiction,
	settings,
	employees,
	periods,
	cola,
	selectedMethod,
	creditBalance,
	principal,
	pensions,
	asset,
}) => {
	const { setup } = settings || {};
	const { length: employeeProgress } = employees || [];
	const { length: periodProgress } = periods || [];

	const isValid = (value) => {
		// force value to string and remove all white spaces using regex
		if (value === undefined || value === null) return false;
		value = String(value).replace(/\s/g, "");
		return !!value;
	};

	const checkAllLocationDetails = () => {
		const list = ["subBuilding", "number", "street", "city", "locality", "county", "country"];

		return list.some((item) => isValid(settings?.address?.[item]));
	};

	const jurisdictionChecklist = {
		UK: {
			"The Basics": {
				"Company Details": {
					"Principal": {
						action: {
							to: "/organisation/settings",
							label: "Edit",
						},
						checked: principal?.title && principal?.givenName && principal?.familyName && principal?.email,
						disabled: true,
						required: true,
					},
					"HMRC Credentials": {
						action: {
							to: "/organisation/settings",
							label: "Edit",
						},
						checked:
							setup?.checked?.["HMRC Credentials"] ||
							(settings?.hmrcCredentials?.senderId && settings?.hmrcCredentials?.hasPassword),
						required: true,
					},
					"PAYE Reference": {
						action: {
							to: "/organisation/settings",
							label: "Edit",
						},
						checked: settings?.payeRef,
						disabled: true,
						required: true,
					},
				},
				"Payroll Details": {
					"Pay Items": {
						action: {
							to: "/organisation/settings/payroll",
							label: "Edit",
						},
						checked: setup?.checked?.["Pay Items"] || settings?.allowances?.length > 0,
						canCheck: true,
						disabled: false,
					},
				},
				"Pension": {
					"Pension Integrations": {
						action: {
							to: "/organisation/settings/integrations#Pensions",
							label: "Edit",
						},
						checked: setup?.checked?.["Pension Integrations"],
						canCheck: true,
						disabled: false,
					},
					"How to add Schemes": {
						action: {
							to: "https://support.buddy.hr/hc/en-gb/articles/10506413216530-Create-a-New-Pension-Scheme",
							label: "Learn More",
						},
						checked: setup?.checked?.["How to add Schemes"],
						canCheck: true,
						disabled: false,
						required: true,
					},
					"Pension Schemes": {
						action: {
							to: "/organisation/pension",
							label: "Edit",
						},
						checked: pensions?.scheme?.list?.length > 0,
						disabled: true,
					},
					"How to add Groups": {
						action: {
							to: "https://support.buddy.hr/hc/en-gb/articles/10506616161938-Create-a-new-Pension-Group",
							label: "Learn More",
						},
						checked: setup?.checked?.["How to add Groups"],
						canCheck: true,
						disabled: false,
						required: true,
					},
					"Pension Groups": {
						action: {
							to: "/organisation/pension",
							label: "Edit",
						},
						checked: pensions?.group?.list?.length > 0,
						disabled: true,
					},
				},
				"Assets": {
					"Read Assets Article": {
						action: {
							to: "https://support.buddy.hr/hc/en-gb/articles/8753478183314-All-You-Need-to-Know-About-Assets",
							label: "Learn More",
						},
						checked: setup?.checked?.["Read Assets Article"],
						canCheck: true,
						disabled: false,
						required: true,
					},
					"Assets": {
						action: {
							to: "/organisation/assets",
							label: "Edit",
						},
						checked: asset?.list?.length > 0,
						disabled: true,
					},
				},
			},
			"The Compliant": {
				"HMRC Notifications": {
					"Read DPS Notifications Article": {
						action: {
							to: "https://support.buddy.hr/hc/en-gb/articles/17492412903314-DPS-HMRC-Notifications",
							label: "Learn More",
						},
						checked: setup?.checked?.["Read DPS Notifications Article"],
						canCheck: true,
						disabled: false,
						required: true,
					},
					"HMRC Notifications": {
						action: {
							to: "/notifications",
							label: "Check",
						},
						checked: setup?.checked?.["HMRC Notifications"],
						canCheck: true,
						disabled: false,
					},
				},
			},
		},
		MT: {
			"The Basics": {
				"Company Details": {
					"PE Number": {
						action: {
							to: "/organisation/settings",
							label: "Edit",
						},
						required: true,
						checked: isValid(settings?.peNumber),
						disabled: true,
					},
					"VAT Number": {
						action: {
							to: "/organisation/settings",
							label: "Edit",
						},
						checked: isValid(settings?.vatNumber),
						disabled: true,
					},
					"Tax Number": {
						action: {
							to: "/organisation/settings",
							label: "Edit",
						},
						checked: isValid(settings?.taxNumber),
						disabled: true,
					},
					"Company Number": {
						action: {
							to: "/organisation/settings",
							label: "Edit",
						},
						checked: isValid(settings?.companyNumber),
						disabled: true,
					},
					"Jobsplus Number": {
						action: {
							to: "/organisation/settings",
							label: "Edit",
						},
						checked: isValid(settings?.jobsplusNumber),
						disabled: true,
					},
				},
				"People": {
					"Guide to Employee Import": {
						action: {
							to: "https://support.buddy.hr/hc/en-mt/articles/360013334659",
							label: "Learn More",
						},
						checked: setup?.checked?.["Guide to Employee Import"],
						canCheck: true,
						disabled: false,
						required: true,
					},
				},
				"Payroll Details": {
					Allowances: {
						action: {
							to: "/organisation/settings/payroll",
							label: "Edit",
						},
						checked: setup?.checked?.["Allowances"] || settings?.allowances?.length > 0,
						canCheck: true,
						disabled: false,
					},
				},
			},
			"The Compliant": {
				"Payroll Import": {
					"Guide to Payroll Import": {
						action: {
							to: "https://support.buddy.hr/hc/en-mt/articles/360012041199-Importing-Previous-Payrolls-into-Buddy",
							label: "Learn More",
						},
						checked: setup?.checked?.["Guide to Payroll Import"],
						canCheck: true,
						disabled: false,
						required: true,
					},
					"Reach Payroll Target": {
						action: {
							to: "/organisation/payroll-history",
							label: "Import Payrolls",
						},
						checked:
							typeof periodProgress !== "undefined" &&
							(periodProgress || 0) >= (setup?.targets?.payPeriods || 0),
						disabled: true,
						required: true,
					},
				},
				"Cost of Living Adjustment": {
					"Guide to COLA": {
						action: {
							to: "https://support.buddy.hr/hc/en-mt/articles/360018705179-Adding-the-Annual-Cost-Of-Living-Adjustment-COLA-",
							label: "Learn More",
						},
						checked: setup?.checked?.["Guide to COLA"],
						canCheck: true,
						disabled: false,
						required: true,
					},
					[`Review COLA for Employees`]: {
						action: {
							to: "/organisation/people",
							label: "Review",
						},
						checked: !cola?.length,
						required: true,
						disabled: true,
					},
				},
				"Leave": {
					"Guide to Leave Adjustments": {
						action: {
							to: "https://support.buddy.hr/hc/en-mt/articles/360011817239-Adjusting-Leave-Balances",
							label: "Learn More",
						},
						checked: setup?.checked?.["Guide to Leave Adjustments"],
						disabled: false,
						required: true,
					},
					"Guide to Bulk Adjustments": {
						action: {
							to: "https://support.buddy.hr/hc/en-mt/articles/9681999691154",
							label: "Learn More",
						},
						checked: setup?.checked?.["Guide to Bulk Adjustments"],
						disabled: false,
					},
					"Review Leave Balance Adjustments": {
						action: {
							to: "/organisation/leave/adjustments",
							label: "Edit",
						},
						checked: setup?.checked?.["Review Leave Balance Adjustments"],
						disabled: false,
						required: true,
					},
				},
			},
		},
	};

	const jurisdictionToApply = jurisdictionChecklist[jurisdiction] || {};

	const checklist = {
		"The Basics": {
			"Company Details": {
				"Company Name": {
					action: {
						to: "/organisation/settings",
						label: "Edit",
					},
					required: true,
					checked: isValid(settings?.name),
					disabled: true,
				},
				"Company Address": {
					action: {
						to: "/organisation/settings",
						label: "Edit",
					},
					checked: checkAllLocationDetails(),
					required: true,
					disabled: true,
				},
				"Company Industry": {
					action: {
						to: "/organisation/settings",
						label: "Edit",
					},
					checked: isValid(settings?.industry),
					disabled: true,
				},
				"Company Logo": {
					action: {
						to: "/organisation/settings",
						label: "Edit",
					},
					checked: !!settings?.image,
					disabled: true,
				},
			},
			"Payroll Details": {
				"Payroll Schedule": {
					action: {
						to: "/organisation/settings/payroll",
						label: "Edit",
					},
					checked: settings?.paySchedules?.length > 0,
					required: true,
					disabled: true,
				},
				"Overtime & Special Rates": {
					action: {
						to: "/organisation/settings/payroll",
						label: "Edit",
					},
					checked: setup?.checked?.["Overtime & Special Rates"] || settings?.overtimes?.length > 0,
					canCheck: true,
					disabled: false,
				},
			},
			"People": {
				"Reach Employee Target": {
					action: {
						to: "/organisation/people",
						label: "Import Employees",
					},
					checked:
						typeof employeeProgress !== "undefined" &&
						(employeeProgress || 0) >= (setup?.targets?.employees || 0),
					disabled: true,
					required: true,
				},
			},
		},
		"The Compliant": {},
		"The Essentials": {
			Billing: {
				"Choose a Plan": {
					action: {
						to: "/organisation/billing",
						label: "Select Plan",
					},
					checked: !!settings?.plan,
					required: true,
					disabled: false,
				},
				"Payment Method": {
					action: {
						to: "/organisation/billing",
						label: "Add Payment Method",
					},
					checked: !!selectedMethod || creditBalance > 0 || !!settings?.paidByAdvisor,
					required: true,
					disabled: true,
				},
				"Billing Email Address": {
					action: {
						to: "/organisation/settings",
						label: "Edit",
					},
					checked: isValid(settings?.billingEmail),
					required: !settings?.paidByAdvisor,
					disabled: true,
				},
			},
		},
	};

	const mergedChecklist = merge(checklist, jurisdictionToApply);

	return Object.entries(mergedChecklist || {}).map(([key, value]) => {
		const remappedSubList = Object.entries(value || {})?.map(([key, value]) => {
			const remappedSubSubList = Object.entries(value || {})
				?.map(([key, value]) => {
					return { title: key, value };
				})
				?.sort((a, b) => {
					if (a.value.required && !b.value.required) return -1;
					if (!a.value.required && b.value.required) return 1;
					if (a.title < b.title) return -1;
					if (a.title > b.title) return 1;
					return 0;
				});

			return {
				title: key,
				tag: camelCase(key),
				list: remappedSubSubList,
				progress: remappedSubSubList?.filter(({ value }) => value.required && value.checked).length,
				target: remappedSubSubList?.filter(({ value }) => value.required).length,
				optionals: remappedSubSubList?.filter(({ value }) => !value.required && value.checked)?.length,
				total: remappedSubSubList?.length,
			};
		});
		return { title: key, list: remappedSubList };
	});
};

const SetTargetModal = ({ closeModal, modalStatus, updateSetupTargets, existingTarget }) => {
	const [value, setValue] = useState(0);

	useEffect(() => {
		setValue(existingTarget || 0);
	}, [modalStatus]);

	if (!modalStatus) return null;

	return (
		<div
			className="modal"
			tabIndex="-1"
			role="dialog"
			aria-hidden="true"
			style={{ display: "block", background: "rgba(0,0,0,0.4)" }}
		>
			<div className="modal-dialog">
				<div className="modal-content">
					<div
						className="block block-themed block-transparent remove-margin-b"
						style={{ padding: 0 }}
					>
						<div className="block-header bg-primary-dark">
							<ul className="block-options">
								<li>
									<button
										data-dismiss="modal"
										type="button"
										onClick={closeModal}
									>
										<XMarkIcon className="h-6 w-6 mr-3 text-white" />
									</button>
								</li>
							</ul>
							<h3
								className="block-title"
								style={{ padding: "15px 20px" }}
							>
								Set Target
							</h3>
						</div>
						<div className="modal-body">
							<div className="clearfix"></div>
							<p>Update Target for {modalStatus}</p>
							<div className="form-group">
								<div className="form-material">
									<input
										type="text"
										className="form-control form-input"
										placeholder="Target"
										value={value}
										onChange={(e) => {
											const newValue = validateFloat(e.target.value);
											setValue(parseFloat(newValue) || 0);
										}}
									/>
								</div>
							</div>
							<div className="flex-auto buttons">
								<button
									className="btn btn-success pull-right"
									type="submit"
									onClick={() => {
										updateSetupTargets(camelCase(modalStatus), value);
										closeModal();
										setValue(0);
									}}
								>
									<CheckBadgeIcon
										className="w-7 h-7 mr-3"
										style={{ verticalAlign: "text-bottom" }}
									/>
									Set Target
								</button>
								<div className="clearfix"></div>
							</div>
						</div>
						<div className="clearfix"></div>
					</div>
				</div>
			</div>
		</div>
	);
};

const mapStateToProps = (state) => {
	const industry = state.company?.settings?.industry;

	return {
		cola: state.cola?.sortedEmployees,
		employees: state.employees?.employeeListWithSalary,
		settings: state.company?.settings,
		periods: state.payPeriods?.periods,
		jurisdiction: state.company?.settings?.jurisdiction,
		industry: industryOptions?.find((item) => item.value === industry)?.label || "Industry",
		selectedMethod: state.company?.settings?.defaultCard,
		creditBalance: state.payment?.stats?.creditBalance,
		companyName: state.company?.settings?.name || "Company Name",
		principal: state.company?.settings?.principal,
		pensions: state.pensions,
		asset: state.asset,
	};
};

const mapDispatchToProps = (dispatch) => ({
	updateCompanyDetails: (body) => dispatch(COMPANY_ACTIONS.updateCompanyDetails(body, true)),
	employeesGetListWithSalary: (body) => dispatch(EMPLOYEE_ACTIONS.employeesGetListWithSalary(body)),
	getCola: () => dispatch(EMPLOYEE_ACTIONS.getCola()),
	showPricingModal: (isNew) => dispatch(PAYMENT_ACTIONS.showPricingModal(true, isNew ?? false)),
	updateSetupProgress: (progress) => {
		dispatch(COMPANY_ACTIONS.updateSetupProgress(progress));
		dispatch(COMPANY_ACTIONS.updateSetupSteps());
	},
	updateSetupToggles: (toggle) => {
		dispatch(COMPANY_ACTIONS.updateSetupToggles(toggle));
		dispatch(COMPANY_ACTIONS.updateSetupSteps());
	},
	updateSetupTargets: (target, value) => {
		dispatch(COMPANY_ACTIONS.updateSetupTargets(target, value));
		dispatch(COMPANY_ACTIONS.updateSetupSteps());
	},
});

export default connect(mapStateToProps, mapDispatchToProps)(SetupChecklist);
